import './VoteQuestionExecutor.scss';
import React, { useEffect, useState } from 'react';
import { ParticipantVoteType } from '@models/document/actionsExecution/ParticipantVoteType';
import { useTypedSelector } from '@hooks/useTypedSelector';
import { IAbookOptions } from '@models/Forms/IAbookOptions';
import { IFormValues } from '@models/Forms/IForms';
import { getABookFormValues } from '@utils/abookUtils';
import { DocumentExecutionService } from '@services/actions/DocumentExecutionService';
import { IVoteResult } from '@models/document/actionsExecution/IVotingModel';
import { useForm } from 'react-hook-form';
import ModalContainer, { IModalContainerProps } from '@molecules/ModalContainer/ModalContainer';
import FormGroup from '@srm/ui/form-group';
import Abookpicker from '@atoms/Abookpicker';
import { IAddressBookData } from '@models/addressbook/IAddressBookData';
import Radio from '@srm/ui/radio';
import TextareaControl from '@srm/ui/controls/TextareaControl';
import { requiredTextRules } from '@srm/ui/lib/requiredTextRules';
import { IVoteProps } from './IVoteProps';
import { useUnsavedFormInput } from '@srm/ui/hooks/useUnsavedFormInput';
import { stringifyError } from '@srm/core/errors';
import { useDocumentAbookTabs } from '@/hooks/useDocumentAbookTabs';

interface IVoteFormData {
    comment?: string;
}

export const VoteQuestionExecutorModal: React.FC<IVoteProps> = (props: IVoteProps) => {
    const [errorText, setErrorText] = useState<string>();
    // Подготовка (модель голосования) и отправка голоса — разные состояния: подготовку можно
    // бросить закрытием окна, отправку — нет.
    const [loading, setLoading] = useState<boolean>(true);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const [decision, setDecision] = useState<ParticipantVoteType>();
    const [participantId, setParticipantId] = useState<number>();
    const [participantName, setParticipantName] = useState<string>();
    const user = useTypedSelector((state) => state.auth.user);

    const [abookOptions, setAbookOptions] = useState<IAbookOptions>();
    const [formValues, setFormValues] = useState<string>('{}');
    const [docIds, setDocIds] = useState<string[]>([]);
    const abookTabs = useDocumentAbookTabs(docIds[0], abookOptions?.tabs);
    const activated = React.useRef(false);

    const formMethods = useForm<IVoteFormData>({ mode: 'onBlur' });
    const hasUnsavedInput = useUnsavedFormInput(formMethods, { textFields: ['comment'] });

    const commentIsRequired = decision === ParticipantVoteType.No;

    const getFormValues = async (docId: string, fValues?: IFormValues) => {
        const vals = await getABookFormValues(docId, fValues);
        if (activated.current && vals) {
            setFormValues(vals);
        }
    };

    useEffect(() => {
        // Закрытое окно ответ подготовки не применяет.
        let ignore = false;
        setLoading(true);
        activated.current = true;

        const serv = new DocumentExecutionService(props.docId);
        serv.getVotingModel(props.action.key)
            .then((res) => {
                if (ignore) return;
                const dict = res.data;
                const docIds = Object.keys(dict);
                const firstDocId = docIds[0];
                const abookOptions = dict[firstDocId].aBookOptions;

                setAbookOptions(abookOptions);
                void getFormValues(firstDocId, abookOptions?.formValues);
                setDocIds(docIds);
            })
            .catch((error) => {
                if (!ignore) setErrorText(error);
            })
            .finally(() => {
                if (!ignore) setLoading(false);
            });

        // Если голосующий Участник комиссии, то берем голос из конфигурации и записываем голосующим текущего пользователя
        if (props.userType === 'participant') {
            switch (props.participantVote) {
                case 'yes':
                    setDecision(ParticipantVoteType.Yes);
                    break;
                case 'no':
                    setDecision(ParticipantVoteType.No);
                    break;
            }

            setParticipantId(+user.id);
            setParticipantName(user.fullName);
        }
        return () => {
            ignore = true;
            activated.current = false;
        };
    }, []);

    const onDecisionChanged = (e: any) => {
        const num = +e.target.value;
        const vote = num;
        setDecision(vote);
    };

    const onSubmit = async (form: IVoteFormData) => {
        if (decision !== undefined && participantId) {
            setErrorText(undefined);
            try {
                setIsSubmitting(true);
                const voteDataRecord: Record<string, IVoteResult> = {};

                docIds.forEach((docId) => {
                    voteDataRecord[docId] = {
                        voteType: decision,
                        comment: form.comment ?? '',
                        participantId: participantId,
                    };
                });

                const execServ = new DocumentExecutionService(props.docId);
                await execServ.sendVote(props.action.key, voteDataRecord);

                if (props.completeHandler) props.completeHandler(true);
            } catch (error) {
                setErrorText(stringifyError(error));
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const modalContainerProps: IModalContainerProps = {
        header: props.action.displayName,
        size: props.modalSize,
        showOkButton: true,
        showCancelButton: true,
        okButtonText: props.okButtonText,
        cancelButtonText: props.cancelButtonText,
        loading: loading || isSubmitting,
        closeDisabled: isSubmitting,
        hasUnsavedInput: () =>
            hasUnsavedInput() ||
            (props.userType === 'secretary' && participantId !== undefined) ||
            ((props.userType === 'secretary' || props.participantVote === 'selectable') && decision !== undefined),
        errorText: errorText,
        warningText: docIds?.length > 1 ? 'Голос будет применен ко всем выбранным вопросам!' : undefined,
        okButtonDisabled: (props.userType === 'secretary' && !participantId) || decision === undefined,
        formMethods: formMethods,
        onSubmit: onSubmit,
        onCancel: () => {
            if (props.completeHandler) props.completeHandler(false);
        },
    };

    return (
        <ModalContainer {...modalContainerProps}>
            {props.userType === 'secretary' && (
                <FormGroup label="Участник комиссии" className="frmGroup">
                    <Abookpicker
                        isMultiple={false}
                        title="Участник комиссии"
                        displayValue={participantName ?? ''}
                        formValues={formValues}
                        name="participant"
                        showChips={false}
                        tabsSettings={abookTabs}
                        onChange={async (value: IAddressBookData[]) => {
                            if (value && value.length === 1) {
                                setParticipantId(value[0].key);
                                setParticipantName(value[0].fullName);
                            }
                        }}
                    />
                </FormGroup>
            )}
            {(props.userType === 'secretary' ||
                (props.userType === 'participant' && props.participantVote === 'selectable')) && (
                <FormGroup label="Решение" className="frmGroup">
                    <Radio name="decision" label="За" value="1" onChange={onDecisionChanged} />
                    <Radio name="decision" label="Против" value="0" onChange={onDecisionChanged} />
                </FormGroup>
            )}

            <TextareaControl
                name="comment"
                label="Комментарий"
                required={commentIsRequired}
                rows={6}
                control={formMethods.control}
                rules={commentIsRequired ? requiredTextRules() : undefined}
            />
        </ModalContainer>
    );
};
